import { useState } from "react";
import { Link } from "react-router-dom";

import { useLive } from "../lib/use-live";
import { useAuth } from "../context/auth";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import {money} from "../lib/format";
import { useRelative, useDateTime } from "../lib/use-relative";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Panel,
  RowActions,
  Table,
  Td,
  TestBadge,
  Tr,
  UserStatusBadge,
} from "../components/ui";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import {
  CreatePlayerDialog,
  DeletePlayerDialog,
  EditPlayerDialog,
} from "../components/player-dialogs";
import { CreditDialog } from "../components/credit-dialogs";
import type { AdminUser, Paged } from "../types/api";

const STATUS_VALUES = ["", "pending_review", "active", "banned", "rejected"] as const;

export function Players() {
  const dateTime = useDateTime();
  const t = useT();
  const relative = useRelative();
  const statusOptions = STATUS_VALUES.map((value) => ({
    value,
    label: t(`statusOption.${value || "any"}` as TranslationKey),
  }));
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filters = useFilters({ q: "", status: "" }, () => setPage(1));
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const players = useLive<Paged<AdminUser>>("players", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        q: filters.applied.q || undefined,
        status: filters.applied.status || undefined,
      });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [creditFor, setCreditFor] = useState<AdminUser | null>(null);

  return (
    <div className="space-y-5">
      <FilterBar
        loading={players.loading}
        state={filters}
        filters={[
          {
            key: "q",
            label: t("common.username"),
            placeholder: t("players.filter.usernamePlaceholder"),
          },
          {
            key: "status",
            label: t("common.status"),
            type: "select",
            options: statusOptions,
          },
        ]}
      />

      <Panel
        eyebrow={t("section.people")}
        title={t("players.title")}
        bodyClassName=""
        actions={
          <>
            <Button size="sm" onClick={players.reload}>
              {t("action.refresh")}
            </Button>
            {can("admin") ? (
              <Button
                size="sm"
                variant="gold"
                onClick={() => setCreating(true)}
              >
                {t("players.new")}
              </Button>
            ) : null}
          </>
        }
      >
        {players.error ? (
          <div className="p-5">
            <ErrorNote message={players.error} onRetry={players.reload} />
          </div>
        ) : (
          <>
            <Table
              minWidth="64rem"
              head={[
                <SortHeader key="u" field="username" state={sort}>
                  {t("common.player")}
                </SortHeader>,
                <SortHeader key="n" field="fullName" state={sort}>
                  {t("common.fullName")}
                </SortHeader>,
                t("common.referralCode"),
                <SortHeader key="s" field="status" state={sort}>
                  {t("common.status")}
                </SortHeader>,
                <SortHeader
                  key="b"
                  field="balanceCents"
                  state={sort}
                  align="right"
                >
                  {t("common.balance")}
                </SortHeader>,
                <SortHeader key="c" field="createdAt" state={sort}>
                  {t("common.joined")}
                </SortHeader>,
                <SortHeader key="l" field="lastLoginAt" state={sort}>
                  {t("common.lastSeen")}
                </SortHeader>,
                <span key="a" className="block text-right">
                  {t("col.actions")}
                </span>,
              ]}
            >
              {players.loading ? (
                <LoadingRows columns={8} />
              ) : players.data && players.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <EmptyState
                      title={t("players.empty")}
                      detail={t("common.noMatch")}
                    />
                  </Td>
                </Tr>
              ) : (
                (players.data?.items ?? []).map((user) => (
                  <Tr key={user.id}>
                    <Td>
                      <Link
                        to={`/players/${user.id}`}
                        className="flex items-center gap-2 font-semibold hover:underline"
                      >
                        {user.username}
                        {user.isTest ? <TestBadge /> : null}
                      </Link>
                    </Td>
                    <Td className="text-muted">{user.fullName}</Td>
                    <Td>
                      {user.referralCode ? (
                        <Link to={`/referrals?q=${user.referralCode.code}`}>
                          <Badge tone="gold">{user.referralCode.code}</Badge>
                        </Link>
                      ) : (
                        <span className="text-dim text-[12px]">—</span>
                      )}
                    </Td>
                    <Td>
                      <UserStatusBadge status={user.status} />
                    </Td>
                    <Td className="money accent text-right">
                      {money(user.balanceCents)}
                    </Td>
                    <Td
                      className="text-dim text-[12px]"
                      title={dateTime(user.createdAt)}
                    >
                      {relative(user.createdAt)}
                    </Td>
                    <Td className="text-dim text-[12px]">
                      {relative(user.lastLoginAt)}
                    </Td>
                    <Td>
                      <RowActions>
                        {/* Top up, set balance and withdraw all live behind this
                            one control — see components/credit-dialogs.tsx.
                            Given the gold treatment because it is what an
                            operator opens this screen to do. */}
                        <ActionButton
                          icon={ActionIcon.credit}
                          tone="primary"
                          onClick={() => setCreditFor(user)}
                        >
                          {t("players.credit")}
                        </ActionButton>
                        {can("admin") ? (
                          <>
                            <ActionButton
                              icon={ActionIcon.edit}
                              onClick={() => setEditing(user)}
                            >
                              {t("action.edit")}
                            </ActionButton>
                            <ActionButton
                              icon={ActionIcon.delete}
                              tone="danger"
                              onClick={() => setDeleting(user)}
                            >
                              {t("action.delete")}
                            </ActionButton>
                          </>
                        ) : null}
                      </RowActions>
                    </Td>
                  </Tr>
                ))
              )}
            </Table>

            {players.data ? (
              <PageBar
                page={players.data.page}
                pages={players.data.pages}
                total={players.data.total}
                perPage={perPage}
                onPage={setPage}
                onPerPage={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>

      <CreatePlayerDialog
        open={creating}
        onClose={() => setCreating(false)}
        onDone={players.reload}
      />
      <EditPlayerDialog
        user={editing}
        onClose={() => setEditing(null)}
        onDone={players.reload}
      />
      <DeletePlayerDialog
        user={deleting}
        onClose={() => setDeleting(null)}
        onDone={players.reload}
      />
      <CreditDialog
        user={creditFor}
        onClose={() => setCreditFor(null)}
        onDone={players.reload}
      />
    </div>
  );
}
